import { FC, useState } from "react";

import { Box, Stack } from "@mui/material";

import { useTranslate } from "@/common/hooks/useTranslate";
import { DashboardButton } from "@/dashboard-design-system/DashboardButton";
import {
  DashboardDialog,
  DashboardDialogBody,
  DashboardDialogContent,
  DashboardDialogFooter,
  DashboardDialogHeader,
} from "@/dashboard-design-system/DashboardDialog";
import { DashboardTypography } from "@/dashboard-design-system/Typography/DashboardTypography";
import { Lock as LockIcon } from "@/icons/outlined";

interface TotpRecoveryCodesProps {
  isOpen: boolean;
  onClose: () => void;
  recoveryCodes: string[];
}

export const TotpRecoveryCodes: FC<TotpRecoveryCodesProps> = ({ isOpen, onClose, recoveryCodes }) => {
  const { t } = useTranslate();
  const [hasCopied, setHasCopied] = useState(false);

  const handleCopy = async () => {
    await navigator.clipboard.writeText(recoveryCodes.join("\n"));
    setHasCopied(true);
    setTimeout(() => setHasCopied(false), 2000);
  };

  return (
    <DashboardDialog open={isOpen} onClose={onClose} maxWidth="sm" vibe="brand">
      <DashboardDialogBody
        slots={{
          header: (
            <DashboardDialogHeader icon={<LockIcon />} title={t("dashboard", "twoFactor.totp.recoveryCodesTitle")} />
          ),
          content: (
            <DashboardDialogContent>
              <Stack spacing={3}>
                <DashboardTypography variant="body2">
                  {t("dashboard", "twoFactor.totp.recoveryCodesDescription")}
                </DashboardTypography>

                <Box
                  sx={(theme) => ({
                    backgroundColor: theme.palette.surface.neutral.supershy,
                    borderRadius: 1,
                    p: 3,
                  })}
                >
                  <Stack spacing={1} alignItems="center">
                    {recoveryCodes.map((code) => (
                      <DashboardTypography
                        key={code}
                        variant="subtitle2"
                        sx={{
                          fontFamily: "monospace",
                          letterSpacing: 2,
                        }}
                        data-test="recovery-code"
                      >
                        {code}
                      </DashboardTypography>
                    ))}
                  </Stack>
                </Box>

                <DashboardTypography variant="caption" sx={{ color: "text.secondary" }}>
                  {t("dashboard", "twoFactor.totp.recoveryCodesWarning")}
                </DashboardTypography>
              </Stack>
            </DashboardDialogContent>
          ),
          footer: (
            <DashboardDialogFooter>
              <DashboardButton data-test="done-recovery-codes-button" fullWidth onClick={onClose}>
                {t("dashboard", "twoFactor.totp.recoveryCodesDone")}
              </DashboardButton>
              <DashboardButton data-test="copy-recovery-codes-button" fullWidth onClick={handleCopy}>
                {hasCopied
                  ? t("dashboard", "twoFactor.totp.recoveryCodesCopied")
                  : t("dashboard", "twoFactor.totp.recoveryCodesCopy")}
              </DashboardButton>
            </DashboardDialogFooter>
          ),
        }}
      />
    </DashboardDialog>
  );
};
